import { motion } from "framer-motion";
import { PropsWithChildren, ReactNode, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";

import ArrowIcon from "@krea/customer-features/images/svg/arrow-down-colorless.svg";
import BidItemLabel from "@krea/customer-features/shared-components/bid-dashboards/commons/BidItemLabel";
import BidsListItemMobileOtherInfo from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListItemMobileOtherInfo";
import Button from "@krea/customer-features/shared-components/button";
import { Text } from "@krea/customer-features/shared-components/text";
import { ILenderCompany } from "@krea/customer-features/types/lenderCompany";
import {
  ILenderResponse,
  IPromotion,
} from "@krea/customer-features/types/lenderResponse";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { PromotionTypeEnum } from "@krea/shared/types/common";

import { BidItemContext } from "./BidItem";
import { getPromotionsAsText } from "./utils";

const motionArrow = {
  hidden: {
    transform: "rotateZ(0deg)",
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
  visible: {
    transform: "rotateZ(180deg)",
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const motionContainer = {
  hidden: {
    opacity: 0,
    height: 0,
  },
  visible: {
    opacity: 1,
    height: "auto",
  },
};

interface IBidsListItemMobile {
  isSelected: boolean;
  isRecommended: boolean;
  isGoodAlternative: boolean;
  lenderResponse: ILenderResponse;
  lenderCompany: ILenderCompany;
  amount: string;
  paybackTime?: string;
  monthlyCost?: string;
  commissionPercent?: string;
  bidDetails: ReactNode;
  showDetails: boolean;
  children: ReactNode;
  detailsButtonText: string;
  className?: string;
  isLeasing?: boolean;
  isPaidOut?: boolean;
  isPaidOutPage?: boolean;
}

const BidsListItemMobile = ({
  isSelected,
  isRecommended,
  isGoodAlternative,
  lenderResponse,
  lenderCompany,
  amount,
  paybackTime,
  monthlyCost,
  commissionPercent,
  bidDetails,
  showDetails,
  children,
  detailsButtonText,
  className,
  isLeasing,
  isPaidOut,
  isPaidOutPage,
}: PropsWithChildren<IBidsListItemMobile>) => {
  const { t } = useTranslation();
  const context = useContext(BidItemContext);

  let labelText,
    accentColor: "mint" | "primary" | undefined = undefined;

  if (!isPaidOutPage) {
    if (isPaidOut) {
      labelText = t("dash.paidOutBidLabel");
      accentColor = "mint";
    } else if (isSelected) {
      labelText = t("dash.selectedBidLabel");
      accentColor = "mint";
    } else if (isRecommended) {
      labelText = t("dash.recommendedBidLabel");
      accentColor = "primary";
    } else if (isGoodAlternative) {
      labelText = t("dash.goodAlternativeBidLabel");
    }
  }

  return (
    <div
      className={twMerge(
        "tw-bg-white tw-border tw-border-solid tw-rounded-xl tw-mb-[25px] tw-border-gray-500",
        {
          "tw-border-primary": isRecommended && !isSelected && !isPaidOut,
          "tw-border-mint-500": isSelected || isPaidOut,
          "tw-bg-mint-100": isPaidOut,
        },
        className,
      )}
    >
      <div>
        <div className="tw-float-start tw-mb-[16px]">
          {labelText ? (
            <BidItemLabel text={labelText} accentColor={accentColor} />
          ) : null}
        </div>
      </div>

      <div className={"tw-mt-6 tw-px-4"}>
        <div className="tw-items-center tw-justify-between">
          {lenderCompany.image?.lowResolutionUrl ? (
            <div className="tw-max-w-[100px] tw-shrink-0">
              <img
                src={lenderCompany.image.lowResolutionUrl}
                alt={lenderCompany.name}
                className="tw-block tw-w-full"
              />
            </div>
          ) : (
            <div className="tw-w-full tw-pr-3 tw-font-bold">
              {lenderCompany.name}
            </div>
          )}
        </div>

        <div className="tw-items-start tw-justify-between tw-pt-5">
          <BidsListItemMobileOtherInfo
            className="tw-text-left"
            title={
              !isLeasing
                ? t("dash.loanBids.columns.approvedAmount")
                : t("dash.leasingBids.columns.amount.title")
            }
            value={amount}
          />

          {paybackTime && (
            <BidsListItemMobileOtherInfo
              className="tw-text-right"
              title={
                !isLeasing
                  ? t("dash.loanBids.columns.repaymentTime")
                  : t("dash.leasingBids.columns.repaymentTime.title")
              }
              value={paybackTime}
            />
          )}

          {monthlyCost && (
            <BidsListItemMobileOtherInfo
              className="tw-text-right"
              title={t("dash.loanBids.columns.monthlyCost")}
              value={monthlyCost}
            />
          )}

          {commissionPercent && (
            <BidsListItemMobileOtherInfo
              className="tw-text-right"
              title={t("dash.factoringBids.columns.commissionTime")}
              value={`${commissionPercent}%`}
            />
          )}
        </div>

        <div>{children}</div>
      </div>

      <motion.div
        variants={motionContainer}
        initial="hidden"
        animate={showDetails ? "visible" : "hidden"}
        transition={{
          default: { duration: 0.6, ease: "easeOut" },
        }}
        className={twMerge("tw-overflow-hidden tw-mt-0 tw-w-full")}
      >
        {bidDetails}
      </motion.div>

      <div
        className={twMerge("tw-px-4 tw-pt-4", {
          "tw-mb-5": !isPaidOutPage,
        })}
      >
        <div className="tw-flex tw-flex-col tw-align-middle">
          <div
            role="button"
            tabIndex={0}
            aria-label={detailsButtonText}
            className={twMerge(
              "active:tw-outline-none before:tw-top-0 before:tw-left-0 before:tw-w-full before:tw-h-full before:tw-z-1 before:tw-display-block before:tw-absolute focus:tw-outline-none tw-h-[40px] tw-cursor-pointer tw-flex tw-justify-center tw-align-middle tw-text-primary tw-mb-3 tw-relative",
            )}
            onKeyUp={context.toggleDetails}
            onClick={context.toggleDetails}
          >
            <Text size="lg" className="tw-mr-2 tw-font-bold">
              {detailsButtonText}
            </Text>

            <motion.div
              variants={motionArrow}
              initial="hidden"
              animate={showDetails ? "visible" : "hidden"}
              className={twMerge(
                "tw-relative tw-shrink-0 tw-w-[16px] tw-h-[16px] tw-mx-0 tw-mb-0 tw-mt-[6px]",
              )}
            >
              <SVG
                src={ArrowIcon}
                className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full"
              />
            </motion.div>
          </div>

          {!isPaidOutPage && (
            <Button
              block
              size="lg"
              variant={"contained"}
              onClick={context.chooseBid}
              disabled={!!isPaidOut || context.isSelecting}
            >
              {isSelected
                ? t("dash.chooseBidButtonAfterSelected")
                : t("dash.chooseBidButton")}
            </Button>
          )}
        </div>
        {lenderResponse.promotions && lenderResponse.promotions.length > 0 && (
          <div
            className={twMerge(
              "-tw-mx-4 tw--mb-5 tw-mt-4 tw-rounded-b-xl tw-bg-mint-100 tw-p-2 tw-text-sm",
            )}
          >
            <div className="tw-px-2">
              <span className="tw-text-gray-600">
                {t("dash.promotionTitle")}:{" "}
              </span>
              <span className="tw-font-bold">
                {lenderResponse.promotions
                  .map((it) => {
                    return getPromotionsAsText(it);
                  })
                  .join(", ")}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BidsListItemMobile;
